import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledEducationSection = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 60px 20px 0 20px;

  @media (max-width: 768px) {
    padding: 40px 15px 0 15px;
  }

  @media (max-width: 480px) {
    padding: 30px 10px 0 10px;
  }

  .inner {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 48px;
    justify-content: flex-start;

    @media (max-width: 900px) {
      flex-direction: column;
      align-items: center;
      gap: 32px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: 180px;
  min-width: 120px;
  list-style: none;
  text-align: left;
  margin-right: 32px;

  @media (max-width: 900px) {
    width: 100%;
    min-width: 0;
    margin-right: 0;
    margin-bottom: 20px;
    display: flex;
    overflow-x: auto;
    padding-left: 0;
    justify-content: space-between;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  @media (max-width: 900px)::-webkit-scrollbar {
    display: none;
  }

  li:first-of-type {
    @media (max-width: 600px) {
      margin-left: 0;
    }
    @media (max-width: 480px) {
      margin-left: 0;
    }
  }

  li:last-of-type {
    @media (max-width: 600px) {
      padding-right: 0;
    }
    @media (max-width: 480px) {
      padding-right: 0;
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 20px 2px;
  border-left: 2px solid var(--dark-slate);
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--slate)')};
  font-family: var(--font-sans);
  font-size: var(--fz-xs);
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0 15px 2px;
  }

  @media (max-width: 600px) {
    ${({ theme }) => theme.mixins.flexCenter};
    flex: 1;
    width: calc(100% / 3);
    padding: 12px 8px;
    border-left: 0;
    border-bottom: 2px solid ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--dark-slate)')};
    text-align: center;
    font-size: var(--fz-sm);
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 10px 6px;
    font-size: var(--fz-xs);
  }

  &:hover,
  &:focus {
    background-color: var(--black-light);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: var(--green);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s ease-in-out;
  transition-delay: 0.1s;

  @media (max-width: 600px) {
    display: none;
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  margin-left: 0;
  text-align: left;

  @media (max-width: 900px) {
    margin-left: 0;
    width: 100%;
  }
`;

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 5px;

  ul {
    ${({ theme }) => theme.mixins.fancyList};
  }

h3 {
  margin-bottom: 10px; /* ⬅️ Increase space below title */
  font-size: var(--fz-lg);
  font-weight: 600;
  background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;

  .institution {
    display: block;
    font-size: var(--fz-sm);
    color: var(--green);
    font-weight: 500;
    margin-top: 4px; /* ⬅️ Add top margin for better separation */
  }
}

  .range {
    margin-bottom: 20px;
    color: var(--light-slate);
    font-family: var(--font-sans);
    font-size: var(--fz-xs);
    font-weight: 500;
  }

  .location {
    font-size: var(--fz-xs);
    font-style: italic;
    color: var(--slate);
    margin-bottom: 10px;
  }

  .content {
    color: var(--lightest-slate);
    line-height: 1.4;
    text-align: left;
  }

  .content p {
    margin-bottom: 16px;
    line-height: 1.4;
    color: var(--lightest-slate);
    text-align: left;
  }
`;

const Education = () => {
  const data = useStaticQuery(graphql`
    query {
      education: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/education/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              label
              institution
              location
              range
            }
            html
          }
        }
      }
    }
  `);

  const educationData = data.education.edges;
  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!prefersReducedMotion) sr.reveal(revealContainer.current, srConfig());
  }, []);

  useEffect(() => {
    if (tabs.current[tabFocus]) tabs.current[tabFocus].focus();
  }, [tabFocus]);

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        setTabFocus(prev => (prev === 0 ? educationData.length - 1 : prev - 1));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setTabFocus(prev => (prev === educationData.length - 1 ? 0 : prev + 1));
        break;
      default:
        break;
    }
  };

  return (
    <StyledEducationSection id="education" ref={revealContainer}>
      <h2 className="gradient-text text-xl">Education</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label="Education tabs" onKeyDown={onKeyDown}>
          {educationData &&
            educationData.map(({ node }, i) => {
              const { label } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => setActiveTabId(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i}
                  aria-controls={`panel-${i}`}>
                  <span>{label}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {educationData &&
            educationData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, institution, range, location } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>
                    <h3>
                      <span>{title}</span>
                      <div className="institution"> {institution}</div>
                    </h3>
                    <p className="range">{range}</p>
                    <p className="location">{location}</p>
                    <div
                      className="content"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>
    </StyledEducationSection>
  );
};

export default Education;
